import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { FiPlus, FiSearch, FiEdit, FiTrash2 } from 'react-icons/fi';
import { elevesService } from '../services/api';
import { toast } from 'react-toastify';
import EleveModal from '../components/eleves/EleveModal';
import { useAuth } from '../contexts/AuthContext';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const Eleves = () => {
 // const { user: _user } = useAuth(); // Préfixe _ pour variable intentionnellement inutilisée
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const queryClient = useQueryClient();
  
  const { data: eleves, isLoading, refetch } = useQuery(
    ['eleves', searchTerm],
    () => elevesService.getAll({ search: searchTerm }),
    {
      select: (response) => {
        // Peut être response.data.data, ou response.data selon l'API
        return response.data;
      },
      onError: (error) => {
        toast.error('Erreur lors du chargement des élèves');
      }
    }
  );

  // Pagination sur les élèves : support des deux formats de réponse (array ou {data: array})
  const elevesArray = Array.isArray(eleves?.data) ? eleves.data : (Array.isArray(eleves) ? eleves : []);
  const totalPages = Math.ceil(elevesArray.length / pageSize) || 1;
  const paginatedEleves = elevesArray.slice((page - 1) * pageSize, page * pageSize);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cet élève ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      try {
        await elevesService.delete(id);
        toast.success('Élève supprimé avec succès');
        refetch();
        Swal.fire('Supprimé !', 'L\'élève a été supprimé.', 'success');
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleEdit = (eleve) => {
    setSelectedEleve(eleve);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedEleve(null);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedEleve(null);
    queryClient.invalidateQueries('eleves');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Élèves</h1>
        <ConditionalButton
          entity="eleves"
          permission="create"
          onClick={handleCreate}
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
        >
          <FiPlus />
          <span>Nouvel élève</span>
        </ConditionalButton>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un élève..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Matricule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom complet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sexe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de naissance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Téléphone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type de séjour
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {elevesArray.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Aucun élève trouvé
                  </td>
                </tr>
              ) : (
                paginatedEleves.map((eleve) => (
                  <tr key={eleve.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {eleve.matricule}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {eleve.nom} {eleve.prenom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {eleve.sexe}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {eleve.date_naissance}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {eleve.telephone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {eleve.type_sejour === 'interne' ? 'Interne' : 'Externe'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <ConditionalActions
                        entity="eleves"
                        onEdit={() => handleEdit(eleve)}
                        onDelete={() => handleDelete(eleve.id)}
                        editIcon={FiEdit}
                        deleteIcon={FiTrash2}
                        className="flex space-x-2"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination controls (inspired by AnneesScolairesPage.js) */}
      <div className="flex justify-center items-center gap-1 mt-4 flex-wrap pb-4">
        <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Premier</button>
        <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Précédent</button>
        {(() => {
          const pageBtns = [];
          if (totalPages <= 10) {
            for (let i = 1; i <= totalPages; i++) {
              pageBtns.push(i);
            }
          } else {
            // 1 à 5, ..., totalPages-4 à totalPages
            for (let i = 1; i <= 5; i++) pageBtns.push(i);
            if (page > 7 && page < totalPages - 6) {
              pageBtns.push('...');
              for (let i = page - 1; i <= page + 1; i++) pageBtns.push(i);
              pageBtns.push('...');
            } else {
              pageBtns.push('...');
            }
            for (let i = totalPages - 4; i <= totalPages; i++) {
              if (i > 5) pageBtns.push(i);
            }
          }
          // Nettoie les boutons qui sortent du range
          return [...new Set(pageBtns)].map((p, idx) =>
            p === '...'
              ? <span key={"ellipsis-" + idx} className="mx-0.5 px-2">...</span>
              : <button
                  key={p}
                  className={`px-2 py-1 rounded mx-0.5 text-sm ${p === page ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-primary-100 text-gray-700'}`}
                  onClick={() => setPage(p)}
                  disabled={p === page}
                >{p}</button>
          );
        })()}
        <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Suivant</button>
        <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm">Dernier</button>
      </div>

    {showModal && (
        <EleveModal
          eleve={selectedEleve}
          onClose={() => setShowModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default Eleves;